<template>
  <div class="product-page">
    <!-- Banner Section -->
    <div class="banner-section">
      <div class="banner-container">
        <div class="banner-left">
          <img :src="currentProduct.image"
               :alt="currentProduct.name"
               class="product-img" />
          <div class="product-name">{{ currentProduct.name }}</div>
        </div>
        <div class="banner-right">
          <h1 class="banner-title">{{ $t('product.light640.bannerTitle') }}</h1>
          <div class="feature-list">
            <div class="feature-item"
                 v-for="(item, index) in bannerFeatures"
                 :key="index">
              <div class="feature-icon-wrapper">
                <img :src="item.icon"
                     alt="icon"
                     class="feature-icon" />
              </div>
              <div class="feature-text">{{ item.text }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Product Nav Section -->
    <div class="product-nav-section">
      <div class="nav-container">
        <div class="nav-item"
             v-for="product in productNavList"
             :key="product.id"
             :class="{ active: currentProductId === product.id }"
             @click="switchProduct(product.id)">
          <div class="nav-img-wrapper">
            <img :src="product.image"
                 :alt="product.name"
                 class="nav-img">
          </div>
          <div class="nav-name">{{ product.name }}</div>
        </div>
      </div>
    </div>

    <!-- Advantages Section -->
    <div class="advantages-section">
      <h2 class="section-title">{{ $t('product.light640.sections.advantages') }}</h2>
      <div class="advantage-list">
        <div class="advantage-item"
             v-for="(item, index) in currentAdvantages"
             :key="index"
             :class="{ 'reverse': index % 2 !== 0 }">
          <!-- Text Area -->
          <div class="text-area">
            <div class="tag"
                 :style="{ backgroundColor: item.tagColor }">
              <img src="@/assets/product/advicon.png"
                   alt="icon"
                   class="tag-icon" />
              <span class="tag-title">{{ item.tagTitle }}</span>
            </div>
            <div class="desc">{{ item.desc }}</div>
          </div>

          <!-- Image Area -->
          <div class="image-area">
            <img :src="item.image"
                 :alt="item.tagTitle"
                 class="adv-img" />
          </div>
        </div>
      </div>
    </div>
    <!-- AI Banner Section -->
    <div class="ai-banner">
      <div class="ai-banner-content">
        <h2 class="ai-title">{{ $t('product.light640.sections.aiTitle') }}</h2>
        <p class="ai-subtitle">{{ $t('product.light640.sections.aiSubtitle') }}</p>
      </div>
    </div>

    <!-- Carousel Section -->
    <div class="carousel-section">
      <h2 class="carousel-title">{{ currentCarouselTitle }}</h2>
      <div class="carousel-container">
        <div class="carousel-track"
             :style="trackStyle">
          <!-- 原始列表 -->
          <div class="carousel-item"
               v-for="(item, index) in currentCarouselItems"
               :key="'original-' + index">
            <img :src="item.image"
                 :alt="item.title"
                 class="carousel-img" />
          </div>
          <!-- 复制一份用于无缝循环 -->
          <div class="carousel-item"
               v-for="(item, index) in currentCarouselItems"
               :key="'clone-' + index">
            <img :src="item.image"
                 :alt="item.title"
                 class="carousel-img" />
          </div>
        </div>
      </div>
    </div>

    <!-- Specs Section -->
    <div class="specs-section">
      <h2 class="section-title">{{ $t('product.light640.sections.specsTitle') }}</h2>
      <div class="specs-container">
        <!-- 左列 -->
        <div class="specs-column">
          <div class="spec-row"
               v-for="(item, index) in currentSpecs.left"
               :key="'left-'+index">
            <div class="spec-label">{{ item.label }}</div>
            <div class="spec-value">{{ item.value }}</div>
          </div>
        </div>

        <!-- 右列 -->
        <div class="specs-column">
          <div class="spec-row"
               v-for="(item, index) in currentSpecs.right"
               :key="'right-'+index">
            <div class="spec-label">{{ item.label }}</div>
            <div class="spec-value">{{ item.value }}</div>
          </div>

          <!-- 系统参数组（底部） -->
          <div class="specs-groups">
            <!-- 冷却系统 -->
            <div class="spec-group">
              <div class="spec-group-header">{{ $t('product.light640.specs.groups.waterCoolingTitle') }}</div>
              <div class="spec-row"
                   v-for="(item, index) in currentSpecs.water"
                   :key="'wc-'+index">
                <div class="spec-label">{{ item.label }}</div>
                <div class="spec-value">{{ item.value }}</div>
              </div>
            </div>

            <!-- 气泵参数 -->
            <div class="spec-group">

              <div class="spec-row"
                   v-for="(item, index) in currentSpecs.air"
                   :key="'ap-'+index">
                <div class="spec-label">{{ item.label }}</div>
                <div class="spec-value">{{ item.value }}</div>
              </div>
            </div>

            <!-- 排气系统 -->
            <div class="spec-group">
              <div class="spec-group-header">{{ $t('product.light640.specs.groups.exhaustTitle') }}</div>
              <div class="spec-row"
                   v-for="(item, index) in currentSpecs.exhaust"
                   :key="'ex-'+index">
                <div class="spec-label">{{ item.label }}</div>
                <div class="spec-value">{{ item.value }}</div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProductLight640',
  data() {
    return {
      currentProductId: 'light640',
      // 轮播控制
      scrollPosition: 0,
      scrollSpeed: 1, // 滚动速度，像素/帧
      animationFrameId: null,
    }
  },
  computed: {
    productCopy() {
      const locale = this.$i18n && this.$i18n.locale ? this.$i18n.locale : 'zh-CN'
      const messages =
        this.$i18n && this.$i18n.getLocaleMessage
          ? this.$i18n.getLocaleMessage(locale)
          : {}
      return (messages.product && messages.product.light640) || {}
    },
    advantageAssetsMap() {
      // 图标资源
      const icons = [
        require('@/assets/product/advicon.png'),
        require('@/assets/product/advicon2.png'),
        require('@/assets/product/advicon3.png'),
      ]
      const colors = ['#448EF7', '#FBBC05', '#8BC34A']

      // 构建各机型的优势图片配置
      return {
        light640: [
          {
            tagIcon: icons[0],
            tagColor: colors[0],
            image: require('@/assets/product/640-adv1.png'),
          },
          {
            tagIcon: icons[1],
            tagColor: colors[1],
            image: require('@/assets/product/640-adv2.png'),
          },
          {
            tagIcon: icons[2],
            tagColor: colors[2],
            image: require('@/assets/product/640-adv3.png'),
          },
        ],
        light960: [
          {
            tagIcon: icons[0],
            tagColor: colors[0],
            image: require('@/assets/product/960-adv1.png'),
          },
          {
            tagIcon: icons[1],
            tagColor: colors[1],
            image: require('@/assets/product/960-adv2.png'),
          },
          {
            tagIcon: icons[2],
            tagColor: colors[2],
            image: require('@/assets/product/960-adv3.png'),
          },
        ],
        light1390: [
          {
            tagIcon: icons[0],
            tagColor: colors[0],
            image: require('@/assets/product/1390-adv1.png'),
          },
          {
            tagIcon: icons[1],
            tagColor: colors[1],
            image: require('@/assets/product/1390-adv2.png'),
          },
          {
            tagIcon: icons[2],
            tagColor: colors[2],
            image: require('@/assets/product/1390-adv3.png'),
          },
        ],
        light1610: [
          {
            tagIcon: icons[0],
            tagColor: colors[0],
            image: require('@/assets/product/1610-adv1.png'),
          },
          {
            tagIcon: icons[1],
            tagColor: colors[1],
            image: require('@/assets/product/1610-adv2.png'),
          },
          {
            tagIcon: icons[2],
            tagColor: colors[2],
            image: require('@/assets/product/1610-adv3.png'),
          },
        ],
        // 默认配置（如果没有特定机型配置）
        default: [
          {
            tagIcon: icons[0],
            tagColor: colors[0],
            image: require('@/assets/product/adv1.png'),
          },
          {
            tagIcon: icons[1],
            tagColor: colors[1],
            image: require('@/assets/product/adv2.png'),
          },
          {
            tagIcon: icons[2],
            tagColor: colors[2],
            image: require('@/assets/product/adv3.png'),
          },
        ],
      }
    },
    productNavList() {
      const models = this.productCopy.models || []
      const modelImages = {
        light640: require('@/assets/product/light640.png'),
        light960: require('@/assets/product/light960.png'),
        light1390: require('@/assets/product/light1390.png'),
        light1610: require('@/assets/product/light1610.png'),
      }
      return models.map((model) => ({
        id: model.id,
        name: model.name,
        image: modelImages[model.id] || require('@/assets/product/light640.png'),
      }))
    },
    currentProduct() {
      const current = this.productNavList.find(
        (item) => item.id === this.currentProductId
      )
      return current || this.productNavList[0] || { id: '', name: '', image: '' }
    },
    bannerFeatures() {
      const icon = require('@/assets/product/icon.png')
      const features = this.productCopy.bannerFeatures || []
      return features.map((text) => ({
        icon,
        text,
      }))
    },
    productsData() {
      const models = this.productCopy.models || []
      const baseAdvantages = this.productCopy.advantages || []
      const advantagesByModel = this.productCopy.advantagesByModel || {}
      const baseSpecs = this.productCopy.specs || {}
      const overrides = this.productCopy.modelSpecOverrides || {}
      const carouselByModel = this.productCopy.carouselByModel || {}
      const advantageAssetsMap = this.advantageAssetsMap || {}

      const buildAdvantages = (modelId) => {
        const advantages = advantagesByModel[modelId] || baseAdvantages
        const currentAssets =
          advantageAssetsMap[modelId] || advantageAssetsMap['default'] || []
        return advantages.map((item, index) => {
          const asset = currentAssets[index] || {}
          return {
            tagTitle: item.tagTitle,
            desc: item.desc,
            tagIcon: asset.tagIcon,
            tagColor: asset.tagColor,
            image: asset.image,
          }
        })
      }

      const getCarousel = (modelId) => {
        const carousel = carouselByModel[modelId]
        if (!carousel || !Array.isArray(carousel.items)) return { title: '', items: [] }
        return {
          title: carousel.title || '',
          items: carousel.items.map((item) => ({
            title: item.title || '',
            image: item.image || '',
          })),
        }
      }

      const applySpecOverrides = (rows, modelId) => {
        const modelOverride = overrides[modelId] || {}
        return (rows || []).map((row) => ({
          label: row.label,
          value:
            row.key && modelOverride[row.key] !== undefined ? modelOverride[row.key] : row.value,
          key: row.key,
        }))
      }

      return models.reduce((acc, model) => {
        const carousel = getCarousel(model.id)
        acc[model.id] = {
          advantages: buildAdvantages(model.id),
          carouselTitle: carousel.title,
          carouselItems: carousel.items,
          specs: {
            left: applySpecOverrides(baseSpecs.left, model.id),
            right: applySpecOverrides(baseSpecs.right, model.id),
            water: applySpecOverrides(baseSpecs.water, model.id),
            air: applySpecOverrides(baseSpecs.air, model.id),
            exhaust: applySpecOverrides(baseSpecs.exhaust, model.id),
          },
        }
        return acc
      }, {})
    },
    currentAdvantages() {
      const product = this.productsData && this.productsData[this.currentProductId]
      return product ? product.advantages : [];
    },
    currentCarouselItems() {
      const product = this.productsData && this.productsData[this.currentProductId]
      return product ? product.carouselItems : [];
    },
    currentCarouselTitle() {
      const product = this.productsData && this.productsData[this.currentProductId]
      if (product && product.carouselTitle) return product.carouselTitle
      return this.$t('product.light640.sections.carouselTitle')
    },
    currentSpecs() {
      const product = this.productsData && this.productsData[this.currentProductId]
      // 如果没有找到对应机型数据，返回一个默认的空结构以防报错
      return product ? product.specs : { left: [], right: [], water: [], air: [], exhaust: [] };
    },
    trackStyle() {
      return {
        transform: `translateX(-${this.scrollPosition}px)`
      }
    }
  },
  mounted() {
    this.startCarousel()
  },
  beforeDestroy() {
    this.stopCarousel()
  },
  methods: {
    switchProduct(id) {
      if (id && this.productsData && this.productsData[id]) {
        this.currentProductId = id
        // 切换产品时重置轮播位置，避免视觉跳跃
        this.scrollPosition = 0;
      }
    },
    startCarousel() {
      const animate = () => {
        this.scrollPosition += this.scrollSpeed

        // 假设每个item宽316 + 间距20 = 336px (这里需要根据CSS实际值调整)
        const itemWidth = 336
        const totalWidth = this.currentCarouselItems.length * itemWidth

        // 当滚动到一半（原始列表结束）时，重置回起点，实现无缝
        // 注意：如果列表长度变了，这个逻辑仍然适用
        if (totalWidth > 0 && this.scrollPosition >= totalWidth) {
          this.scrollPosition = 0
        }

        this.animationFrameId = requestAnimationFrame(animate)
      }
      this.animationFrameId = requestAnimationFrame(animate)
    },
    stopCarousel() {
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId)
      }
    }
  }
}
</script>

<style scoped>
.product-page {
  width: 100%;
  background-color: #fff;
  padding-bottom: 80px; /* 增加底部留白 */
}

/* Product Nav Section */
.product-nav-section {
  width: 100%;
  height: 494px;
  background-color: #f7f7f7;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-container {
  display: flex;
  gap: 80px; /* 间距 */
}

.nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.nav-img-wrapper {
  width: 200px; /* 假设图片大小 */
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  /* 可以加一些默认的图片效果 */
}

.nav-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  opacity: 0.8;
  transition: opacity 0.3s;
}

.nav-name {
  font-size: 20px;
  color: #333;
  transition: font-weight 0.3s;
}

.nav-item.active .nav-img {
  opacity: 1;
}

.nav-item.active .nav-name {
  font-weight: bold;
}

/* ... existing styles ... */

/* Banner Section */
.banner-section {
  width: 100%;
  height: 684px;
  background-image: url("@/assets/product/640banner.png"); /* Industrial bg */
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.banner-container {
  width: 1200px;
  height: 406px;
  background: rgba(255, 255, 255, 0.21); /* 半透明背景 */
  border-radius: 10px;
  display: flex;
}

.banner-left {
  width: 532px;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.product-img {
  width: 400px;
  height: auto;
  object-fit: contain;
  margin-bottom: 20px;
}

.product-name {
  font-size: 20px;
  color: #333;
}

.banner-right {
  flex: 1;
  padding: 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.banner-title {
  font-size: 40px;
  font-weight: bold;
  color: #333;
  margin-bottom: 40px;
}

.feature-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 15px;
}

.feature-icon-wrapper {
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.feature-icon {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.feature-text {
  font-size: 20px;
  color: #333;
}

/* Advantages Section */
.advantages-section {
  width: 1200px;
  margin: 0 auto;
  padding-top: 100px;
  padding-bottom: 50px;
}

.section-title {
  font-size: 34px;
  font-weight: bold;
  color: #333;
  text-align: center;
  margin-bottom: 60px;
}

.advantage-list {
  display: flex;
  flex-direction: column;
  gap: 0; /* 无间距，因为是交替布局 */
}

.advantage-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 60px;
}

.advantage-item.reverse {
  flex-direction: row-reverse;
}

.text-area {
  width: 453px;
  height: 286px; /* 容器高度 */
  display: flex;
  flex-direction: column;
  justify-content: flex-start; /* 上对齐 */
}

.tag {
  width: 283px;
  height: 62px;
  border-radius: 65px;
  display: flex;
  align-items: center;
  padding: 0 20px;
  margin-bottom: 20px; /* 间距 */
}

.tag-icon {
  width: 42px;
  height: 42px;
  margin-right: 15px;
  filter: brightness(0) invert(1); /* 变为白色 */
}

.tag-title {
  font-size: 20px;
  color: #fff;
  font-weight: bold;
}

.desc {
  width: 453px;
  height: auto;
  font-size: 16px;
  color: #333;
  line-height: 1.6;
  white-space: pre-line;
}

.image-area {
  width: 577px;
  height: 360px;
  border-radius: 10px;
  overflow: hidden;

  /* 确保文字区域对齐图片区域的中间 */
  /* 在flex父级设置 align-items: center 已经实现 */
}

.adv-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* AI Banner Section */
.ai-banner {
  width: 100%;
  height: 600px;
  background-image: url("@/assets/product/aibanner.png"); /* AI Tech bg */
  background-size: contain;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-banner-content {
  width: 1200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-left: 100px; /* 根据图示大概位置调整 */
}

.ai-title {
  font-size: 34px;
  color: #333;
  margin-bottom: -20px;
  margin-left: -100px;
  font-weight: normal; /* 图示看起来不是特别粗，或者根据需要调整 */
}

.ai-subtitle {
  font-size: 34px;
  color: #333;
  margin-left: -100px;
  font-weight: normal;
}

/* Carousel Section */
.carousel-section {
  width: 100%;
  height: 650px;
  background-color: #f4f6fa;
  padding-top: 80px;
  overflow: hidden; /* 隐藏超出部分 */
}

.carousel-title {
  font-size: 34px;
  font-weight: bold;
  color: #333;
  text-align: center;
  margin-bottom: 60px;
}

.carousel-container {
  width: 100%;
  /* 不需要限制1200px，因为是全屏流式轮播 */
  overflow: hidden;
}

.carousel-track {
  display: flex;
  gap: 20px; /* 间距 */
  width: max-content; /* 让宽度自适应内容 */
  padding-left: 50px; /* 初始偏移一点，避免贴边 */
  will-change: transform; /* 优化性能 */
}

.carousel-item {
  width: 316px;
  height: 316px;
  flex-shrink: 0;
  border-radius: 30px;
  overflow: hidden;
  background: #fff;
}

.carousel-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Specs Section */
.specs-section {
  width: 1200px;
  margin: 0 auto;
  padding-top: 100px;
  padding-bottom: 50px;
}

.specs-container {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.specs-column {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.spec-row {
  display: flex;
  min-height: 50px; /* 确保行高 */
}

/* 隔行变色逻辑需要小心，因为这里是手动分组
   我们可以用 :nth-child 来做简单的条纹，
   或者给每一行单独的样式。UI图看起来是交替的灰色背景。
*/
.spec-row:nth-child(odd),
.spec-value-only:nth-child(odd),
.spec-full-row:nth-child(odd) {
  background-color: #f9f9f9; /* 浅灰 */
}

/* 左侧列统一样式 */
.specs-column:first-child .spec-row:nth-child(odd) {
  background-color: #f9f9f9;
}
.specs-column:first-child .spec-row:nth-child(even) {
  background-color: #fff;
}

/* 右侧列比较复杂，有标题穿插，建议统一给所有直接子元素加条纹 */
.specs-column > div {
  /* 默认白色 */
  background-color: #fff;
}
/* 
  但是右侧有标题行，标题行是灰色的。
  根据图示，标题行(group-header)通常有背景色 #EAEAEA
*/

.spec-group-header {
  background-color: #e6e6e6;
  font-weight: bold;
  color: #333;
  padding: 15px 20px;
  font-size: 16px;
  margin-bottom: 0; /* 紧贴下方内容 */
}

.spec-label {
  width: 180px; /* 标签宽度 */
  padding: 15px 20px;
  color: #333;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.spec-value {
  flex: 1;
  padding: 15px 20px;
  color: #666;
  display: flex;
  align-items: center;
  line-height: 1.5;
}

.spec-value-only {
  padding: 15px 20px;
  color: #666;
  background-color: #fff; /* 或根据条纹 */
}

.spec-full-row {
  padding: 15px 20px;
  color: #666;
  background-color: #f9f9f9; /* 最后一行如果是单行，可能是灰色 */
}

/* 针对左列的条纹调整：第一行是白色还是灰色？通常第一行白色 */
.specs-column:first-child .spec-row:nth-of-type(odd) {
  background-color: #f9f9f9;
}
.specs-column:first-child .spec-row:nth-of-type(even) {
  background-color: #fff;
}

/* 右列条纹：
   需要手动控制或者用复杂的 nth-child。
   为了简单还原UI图，我们假设右侧每个小块内部也是交替的。
   但右侧有 Header 隔断。
   
   简单策略：
   Header: #E6E6E6
   内容行: 交替 #fff / #f9f9f9
*/
.specs-column:last-child .spec-row:nth-of-type(odd) {
  background-color: #fff;
}
.specs-column:last-child .spec-row:nth-of-type(even) {
  background-color: #f9f9f9;
}
/* 特殊处理：气泵下方的纯值行 */
.spec-value-only {
  background-color: #fff;
}
</style>
